import React, {FC, useLayoutEffect, useRef} from 'react'
import {commerciumCartDisplayHeight} from './commerciumCartMarkMetrics'
import {paintCartLoaderToCanvas} from './commerciumCartLoaderEngine'
import {COMMERCIUM_CART_SIZE_PAGE} from './commerciumLoaderSizes'
import CommerciumCartMarkFallback from './CommerciumCartMarkFallback'

type CommerciumCartMarkProps = {
  size?: number
  onDark?: boolean
}

const CommerciumCartMark: FC<CommerciumCartMarkProps> = ({
  size = COMMERCIUM_CART_SIZE_PAGE,
  onDark = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fallbackRef = useRef<HTMLDivElement>(null)
  const displayHeight = commerciumCartDisplayHeight(size)

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined

    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let raf = 0
    let start: number | null = null
    let painted = false

    const paintAt = (timeS: number) => {
      paintCartLoaderToCanvas(canvas, {
        timeS,
        cssWidth: size,
        cssHeight: displayHeight,
        onDark,
        animate: !reducedMotion,
      })
      if (!painted && fallbackRef.current) {
        fallbackRef.current.style.display = 'none'
        painted = true
      }
    }

    paintAt(0)

    const tick = (now: number) => {
      if (start === null) start = now
      paintAt((now - start) / 1000)
      raf = window.requestAnimationFrame(tick)
    }

    raf = window.requestAnimationFrame(tick)

    const onResize = () => {
      start = null
      paintAt(0)
    }
    window.addEventListener('resize', onResize)

    return () => {
      window.cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
    }
  }, [size, displayHeight, onDark])

  return (
    <div
      className='cmm-cart-loader'
      style={
        {
          '--cmm-cart-size': `${size}px`,
          '--cmm-cart-height': `${displayHeight}px`,
        } as React.CSSProperties
      }
      aria-hidden='true'
    >
      <div ref={fallbackRef} className='cmm-cart-loader__fallback' aria-hidden='true'>
        <CommerciumCartMarkFallback size={size} displayHeight={displayHeight} />
      </div>
      <canvas ref={canvasRef} className='cmm-cart-loader__canvas' />
    </div>
  )
}

export default CommerciumCartMark
